import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Network, UserMinus } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import type { ApiError } from '@/core/api';
import { useEmployees } from '@/features/hrm/employees/queries';
import { employeeName, type Employee } from '@/features/hrm/employees/types';

interface TreeNode {
  employee: Employee;
  reports: TreeNode[];
}

/** Build a forest from the flat Employee list using `manager` self-FK. */
function buildForest(employees: Employee[]): { roots: TreeNode[]; orphans: Employee[] } {
  const byId = new Map<number, TreeNode>();
  for (const e of employees) byId.set(e.id, { employee: e, reports: [] });

  const roots: TreeNode[] = [];
  for (const e of employees) {
    const node = byId.get(e.id)!;
    if (e.manager && byId.has(e.manager)) {
      byId.get(e.manager)!.reports.push(node);
    } else {
      roots.push(node);
    }
  }
  // Anyone with a manager not in the active set is an orphan in the chart.
  const orphans: Employee[] = [];
  for (const e of employees) {
    if (e.manager && !byId.has(e.manager)) orphans.push(e);
  }
  return { roots, orphans };
}

function NodeCard({ employee }: { employee: Employee }) {
  return (
    <Card className="bg-card flex w-56 flex-col gap-1 p-3">
      <Link
        to={`/hrm/employees/${employee.id}`}
        className="hover:text-primary truncate text-sm font-medium"
      >
        {employeeName(employee)}
      </Link>
      {employee.job_title ? (
        <p className="text-muted-foreground truncate text-xs">{employee.job_title}</p>
      ) : null}
      {employee.employment_type === 'contractor' ? (
        <Badge variant="outline" className="w-fit text-[10px]">
          Contractor
        </Badge>
      ) : null}
    </Card>
  );
}

function TreeBranch({ node, depth = 0 }: { node: TreeNode; depth?: number }) {
  return (
    <div className={depth === 0 ? 'space-y-3' : 'space-y-2'}>
      <NodeCard employee={node.employee} />
      {node.reports.length ? (
        <div className="border-muted-foreground/30 ml-6 space-y-2 border-l pl-4">
          {node.reports
            .sort((a, b) => employeeName(a.employee).localeCompare(employeeName(b.employee)))
            .map((r) => (
              <TreeBranch key={r.employee.id} node={r} depth={depth + 1} />
            ))}
        </div>
      ) : null}
    </div>
  );
}

export function OrgChartPage() {
  const [showTerminated, setShowTerminated] = useState(false);
  const { data, isPending, isError, error } = useEmployees({
    include_terminated: showTerminated || undefined,
  });

  // Current staff by default (manager FK is on Employee, not Member; contractors
  // included). Toggling "Show terminated" pulls in the full historical structure.
  const employees = useMemo(() => {
    const all = data?.results ?? [];
    return showTerminated ? all : all.filter((e) => e.status === 'active');
  }, [data, showTerminated]);
  const { roots, orphans } = useMemo(() => buildForest(employees), [employees]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Network className="text-muted-foreground size-5" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Org Chart</h1>
            <p className="text-muted-foreground text-sm">
              Reporting structure derived from each Employee&rsquo;s manager.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id="org-chart-show-terminated"
            checked={showTerminated}
            onCheckedChange={setShowTerminated}
          />
          <Label htmlFor="org-chart-show-terminated" className="text-sm font-normal">
            Show terminated
          </Label>
        </div>
      </div>

      {isError ? (
        <p className="text-destructive text-sm">
          {(error as unknown as ApiError)?.message ?? 'Failed to load org chart.'}
        </p>
      ) : isPending ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : roots.length ? (
        <div className="space-y-6">
          {roots
            .sort((a, b) => employeeName(a.employee).localeCompare(employeeName(b.employee)))
            .map((root) => (
              <TreeBranch key={root.employee.id} node={root} />
            ))}
        </div>
      ) : (
        <div className="text-muted-foreground flex flex-col items-center gap-2 rounded-md border border-dashed py-12 text-sm">
          <Network className="size-6" />
          No active employees to chart.
        </div>
      )}

      {orphans.length ? (
        <div className="space-y-2 pt-6">
          <div className="text-muted-foreground flex items-center gap-2 text-sm">
            <UserMinus className="size-4" />
            Without an active manager:
          </div>
          <div className="flex flex-wrap gap-2">
            {orphans.map((e) => (
              <NodeCard key={e.id} employee={e} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
